import api from './api';
import type { PaginatedResponse } from '@/types';

// SPEC-426 / #437-#441: statement batches, policy-level results and disputes.

export type StatementCategory =
  | 'matched'
  | 'short_paid'
  | 'over_paid'
  | 'not_in_books'
  | 'no_calculation';

export type DisputeStatus =
  | 'open'
  | 'raised'
  | 'insurer_agreed'
  | 'insurer_rejected'
  | 'written_off'
  | 'resolved';

export type LineDecision = '' | 'none' | 'accept_statement' | 'accept_books' | 'dispute';

export type DisputeAction = 'raise' | 'agree' | 'reject' | 'resolve' | 'write_off' | 'reopen';

export interface BatchTotals {
  categories: Partial<Record<StatementCategory, number>>;
  stated_total: string;
  expected_total: string;
  difference: string;
  open_disputes: number;
}

export interface StatementBatch {
  id: string;
  insurer: string;
  insurer_name: string;
  format: string;
  period: string;
  source_file: string;
  statement_reference: string;
  tolerance_used: string | null;
  row_count: number;
  error_count: number;
  policy_line_count: number;
  uploaded_by_name: string;
  created_at: string;
  superseded_by: string | null;
  totals: BatchTotals;
}

export interface RateLink {
  calculation: string;
  source: 'commission_grid_row' | 'broker_commission_rate' | 'policy_commission_rate' | null;
  rate_id: string | null;
  grid_version: string | null;
}

export interface LineDispute {
  id: string;
  status: DisputeStatus;
  amount_disputed: string;
  amount_settled: string | null;
}

export interface StatementPolicyLine {
  id: string;
  insurer: string;
  insurer_name: string;
  base_policy_number: string;
  policy: string | null;
  policy_number: string | null;
  calculation: string | null;
  stated_premium: string | null;
  stated_commission: string;
  stated_total: string;
  stated_rate: string | null;
  expected_premium: string | null;
  expected_total: string | null;
  expected_rate: string | null;
  difference: string | null;
  category: StatementCategory;
  category_label: string;
  reason: string;
  flags: string[];
  row_count: number;
  verified_at: string | null;
  decision: LineDecision;
  decided_at: string | null;
  dispute: LineDispute | null;
  rate_link: RateLink | null;
}

export interface CategorySummary {
  total_count: number;
  categories: Record<StatementCategory, { count: number; stated_total: string; expected_total: string; difference: string }>;
  labels: Record<StatementCategory, string>;
}

export interface NettedRow {
  id: string;
  row_number: number | null;
  kind: string;
  endorsement_ref: string;
  transaction_ref: string;
  date: string;
  premium: string | null;
  commission: string | null;
  reward: string | null;
  total: string | null;
  flags: string[];
}

export interface Dispute {
  id: string;
  policy_line: string;
  status: DisputeStatus;
  status_label: string;
  amount_disputed: string;
  amount_settled: string | null;
  insurer_reference: string;
  allowed_actions: DisputeAction[];
  suggest_resolve: boolean;
}

export interface HistoryItem {
  type: 'transition' | 'decision' | 'note' | 'revision';
  at: string;
  by: string;
  from: string;
  to: string;
  note: string;
  share_with_insurer: boolean;
}

export interface DisputeSummary {
  by_status: Record<DisputeStatus, { count: number; amount_disputed: string; amount_settled: string }>;
  total_count: number;
  disputed: string;
  recovered: string;
}

export interface BulkResult {
  succeeded: number;
  failed: number;
  results: { id: string; ok: boolean; reason?: string }[];
}

export interface LineFilters {
  batch?: string;
  insurer?: string;
  category?: string;
  flag?: string;
  decision?: string;
  dispute_status?: string;
  search?: string;
  ordering?: string;
  page?: number;
}

export interface BatchFilters {
  insurer?: string;
  period?: string;
  status?: string;
  page?: number;
}

export interface ExportFilters {
  insurer?: string;
  batch?: string;
  category?: string;
  dispute_status?: string;
  date_from?: string;
  date_to?: string;
}

const BASE = '/commissions';

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') p.append(k, String(v));
  });
  return p.toString();
}

export const statementVerificationService = {
  async listBatches(f: BatchFilters = {}): Promise<PaginatedResponse<StatementBatch>> {
    const res = await api.get(`${BASE}/statement-batches/?${qs({ ...f })}`);
    return res.data;
  },
  async getBatch(id: string): Promise<StatementBatch> {
    const res = await api.get(`${BASE}/statement-batches/${id}/`);
    return res.data;
  },
  async recheckBatch(id: string) {
    const res = await api.post(`${BASE}/statement-batches/${id}/recheck/`);
    return res.data;
  },
  async listLines(f: LineFilters = {}): Promise<PaginatedResponse<StatementPolicyLine>> {
    const res = await api.get(`${BASE}/statement-policy-lines/?${qs({ ...f, page_size: 100 })}`);
    return res.data;
  },
  async lineSummary(f: LineFilters = {}): Promise<CategorySummary> {
    const { page: _page, ordering: _ordering, ...rest } = f;
    void _page;
    void _ordering;
    const res = await api.get(`${BASE}/statement-policy-lines/summary/?${qs({ ...rest })}`);
    return res.data;
  },
  async lineRows(id: string): Promise<NettedRow[]> {
    const res = await api.get(`${BASE}/statement-policy-lines/${id}/rows/`);
    return res.data;
  },
  async lineHistory(id: string): Promise<HistoryItem[]> {
    const res = await api.get(`${BASE}/statement-policy-lines/${id}/history/`);
    return res.data;
  },
  async recheckLine(id: string) {
    const res = await api.post(`${BASE}/statement-policy-lines/${id}/recheck/`);
    return res.data;
  },
  async decide(
    id: string,
    body: { decision: LineDecision; note?: string; share_with_insurer?: boolean; amount_disputed?: string },
  ): Promise<StatementPolicyLine> {
    const res = await api.post(`${BASE}/statement-policy-lines/${id}/decision/`, body);
    return res.data;
  },
  async getDispute(id: string): Promise<Dispute> {
    const res = await api.get(`${BASE}/statement-disputes/${id}/`);
    return res.data;
  },
  async transition(
    id: string,
    body: {
      action: DisputeAction;
      note: string;
      share_with_insurer?: boolean;
      amount_settled?: string;
      insurer_reference?: string;
      from_status?: DisputeStatus;
    },
  ): Promise<Dispute> {
    const res = await api.post(`${BASE}/statement-disputes/${id}/transition/`, body);
    return res.data;
  },
  async bulk(body: {
    ids?: string[];
    policy_line_ids?: string[];
    action: DisputeAction | 'open';
    note?: string;
    share_with_insurer?: boolean;
    amount_settled?: string;
  }): Promise<BulkResult> {
    const res = await api.post(`${BASE}/statement-disputes/bulk-transition/`, body);
    return res.data;
  },
  async disputeSummary(f: { batch?: string; insurer?: string } = {}): Promise<DisputeSummary> {
    const res = await api.get(`${BASE}/statement-disputes/summary/?${qs({ ...f })}`);
    return res.data;
  },
  async exportDisputes(f: ExportFilters, format: 'csv' | 'xlsx'): Promise<Blob> {
    const res = await api.get(`${BASE}/statement-policy-lines/export/?${qs({ ...f, format })}`, {
      responseType: 'blob',
    });
    return res.data;
  },
  async markExportedRaised(f: ExportFilters): Promise<{ raised: number; failed: unknown[] }> {
    const res = await api.post(`${BASE}/statement-policy-lines/export-mark-raised/`, f);
    return res.data;
  },
};
